<template>
    <v-form v-if="this.user != ''" class="my-0">
      <v-container fluid>
        <v-card dark class="mx-auto my-2">
          <v-card-title>{{
            t("Status", "Status")
          }}</v-card-title>
          <v-card-subtitle>{{ email }}</v-card-subtitle>

          <!-- Overview Section -->
          <v-card color="blue-grey darken-3" dark class="mx-4 my-4 pa-3">
            <v-row align="center" class="mb-2">
              <v-col class="py-0">
                <v-card-subtitle class="pa-0">Overview</v-card-subtitle>
              </v-col>
              <v-col cols="auto" class="py-0">
                <v-btn
                  small
                  text
                  dark
                  :loading="refreshing"
                  @click="refreshData"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col
                v-for="(item) in devicesByOwner"
                :key="'overview-' + item.id"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-chip
                  class="ma-1 overview-chip"
                  :color="item.loading ? 'grey' : (item.isConnected ? (item.currentWiFi > -70 ? 'green' : 'orange') : 'red')"
                  dark
                  small
                  @click="scrollToDevice(item.id)"
                >
                  <div>
                    <div>
                      <v-icon left x-small>{{ item.loading ? 'mdi-loading mdi-spin' : (item.isConnected ? 'mdi-wifi' : 'mdi-wifi-off') }}</v-icon>
                      {{ item.name }}
                    </div>
                    <div v-if="item.isConnected && !item.loading" class="chip-stats">
                      {{ item.currentWiFi }} dBm<span v-if="item.currentFreeGb != null"> · {{ item.currentFreeGb }}GB</span><span v-if="item.currentVoltage != null"> · {{ item.currentVoltage }}V</span><span v-if="item.lastStatusTime"> · {{ formatLastSeen(item.lastStatusTime) }}</span>
                    </div>
                    <div v-else-if="!item.loading && item.lastStatusTime" class="chip-stats">{{ formatLastSeen(item.lastStatusTime) }}</div>
                    <div v-else-if="!item.loading" class="chip-stats">>{{ statusLookbackDays }} days ago</div>
                  </div>
                </v-chip>
              </v-col>
            </v-row>
          </v-card>

          <!-- Device Cards -->
          <v-row>
            <v-col
              v-for="(item) in devicesByOwner"
              :key="item.componentKey"
              cols="12"
              md="6"
            >
              <v-card
                :id="'device-' + item.id"
                color="blue-grey darken-4"
                dark
                class="mx-2 my-2"
                max-width="100%"
              >
                <v-card-title class="py-2">
                  {{ item.name }}
                  <v-spacer></v-spacer>
                  <v-chip x-small :color="item.isConnected ? (item.currentWiFi > -70 ? 'green' : 'orange') : 'red'">
                    {{ item.isConnected ? `${item.currentWiFi} dBm` : 'Offline' }}
                  </v-chip>
                </v-card-title>
                <v-card-text class="py-1 caption">
                  v{{ item.version }}
                  <span v-if="!item.loading && !item.isConnected && item.lastStatusTime" class="red--text text--lighten-1">
                    · {{ formatLastSeen(item.lastStatusTime) }}
                  </span>
                  <span v-else-if="!item.loading && !item.isConnected" class="red--text text--lighten-1">
                    · last seen >{{ statusLookbackDays }} days ago
                  </span>
                </v-card-text>

                <!-- Loading indicator -->
                <v-progress-linear
                  v-if="item.loading"
                  indeterminate
                  color="primary"
                  class="mb-2"
                ></v-progress-linear>

                <!-- Default WiFi Signal Chart -->
                <div v-if="!item.loading && item.hasData && item.signalSeries.length > 0" class="px-3 pb-2">
                  <div class="chart-container-compact">
                    <div class="chart-title-compact">WiFi Signal (dBm)</div>
                    <line-chart
                      :colors="['#9c27b0']"
                      :height="100"
                      :library="getCompactChartOptions()"
                      :data="item.signalSeries"
                    ></line-chart>
                  </div>
                </div>

                <!-- No data message -->
                <v-alert v-if="!item.loading && !item.hasData" type="info" dense class="ma-2 caption">
                  No recent data
                </v-alert>

                <!-- Expand button for more charts -->
                <v-card-actions v-if="!item.loading && item.hasData" class="pt-0">
                  <v-btn
                    text
                    x-small
                    color="primary"
                    @click="toggleExpanded(item.id)"
                  >
                    <v-icon left x-small>{{ expandedDevices[item.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    {{ expandedDevices[item.id] ? 'Hide Charts' : 'More Charts' }}
                  </v-btn>
                </v-card-actions>

                <!-- Expanded Charts Section -->
                <v-expand-transition>
                  <div v-if="expandedDevices[item.id] && !item.loading && item.hasData" class="px-3 pb-3">
                    <v-divider class="mb-3"></v-divider>
                    <div class="caption mb-2 grey--text">Timezone: {{ item.timezone }}</div>
                    <div
                      v-for="(chart, index) in getAvailableCharts(item)"
                      :key="chart.key"
                      :class="index < getAvailableCharts(item).length - 1 ? 'mb-2' : ''"
                    >
                      <div class="chart-container">
                        <div class="chart-title">{{ chart.title }}</div>
                        <line-chart
                          :colors="[chart.color]"
                          :height="index === getAvailableCharts(item).length - 1 ? 120 : 100"
                          :library="getChartOptions(index === getAvailableCharts(item).length - 1, chart.yLabel)"
                          :data="chart.data"
                        ></line-chart>
                      </div>
                    </div>
                  </div>
                </v-expand-transition>

              </v-card>
            </v-col>
          </v-row>

        </v-card>
      </v-container>
      <v-card>
        <v-btn to="/privacy">{{ t("Privacy", "Privacy") }}</v-btn>
        <v-btn to="/terms">{{ t("Terms", "Terms") }}</v-btn>
      </v-card>
    </v-form>
  </template>

  <script>
  import { API } from "aws-amplify";
  import { Auth } from "aws-amplify";
  import {
    haikuboxByOwner,
    statusByDate,
  } from "../graphql/queries";

  var moment = require("moment-timezone");
  var tzlookup = require("tz-lookup");

  export default {
    name: "Settings",
    props: ["user", "email"],
    created() {
      // get language from local storage or browser
      const haikuboxLanguage = localStorage.getItem("haikuboxLanguage");
      if (haikuboxLanguage != null) {
        this.setLanguage(haikuboxLanguage);
      } else {
        let browserLanguage = navigator.language || navigator.languages[0];
        let browserLang = browserLanguage.split("-")[0];
        this.setLanguage(browserLang);
      }
    },
    mounted() {
      if (this.user == "") {
        setTimeout(async () => {
          await this.getDevicesByOwner();
        }, 2000);
      } else {
        this.getDevicesByOwner();
      }

      if (navigator.userAgent.indexOf("gonative") > -1) {
        this.goNativeActive = true;
      }
    },
    data() {
      return {
        devicesByOwner: [],
        numberOfDevices: null,
        goNativeActive: false,
        haikuboxesLoaded: false,
        selectedLanguage: "en",
        currentTranslations: {},
        expandedDevices: {},
        refreshing: false,
        statusLookbackDays: 7, // How far back we query status records
      };
    },
    methods: {
      setLanguage(lang) {
        if (
          (lang != "da") &
          (lang != "de") &
          (lang != "en") &
          (lang != "es") &
          (lang != "fi") &
          (lang != "fr") &
          (lang != "it") &
          (lang != "nl") &
          (lang != "no") &
          (lang != "pt") &
          (lang != "sv")
        ) {
          lang = "en";
        }
        this.loadTranslations(lang).then((translations) => {
          this.currentTranslations = translations;
        });
        this.selectedLanguage = lang;
      },
      loadTranslations(lang) {
        return fetch(`/I18N/${lang}.json`)
          .then((response) => response.json())
          .then((translations) => {
            return translations;
          });
      },
      t(key, defaultText) {
        return this.currentTranslations[key] || defaultText;
      },
      toggleExpanded(deviceId) {
        this.$set(this.expandedDevices, deviceId, !this.expandedDevices[deviceId]);
      },
      scrollToDevice(deviceId) {
        const element = document.getElementById('device-' + deviceId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      },
      async refreshData() {
        this.refreshing = true;
        this.devicesByOwner = [];
        await this.getDevicesByOwner();
        this.refreshing = false;
      },
      getCompactChartOptions() {
        return {
          layout: {
            padding: { left: 5, right: 5, top: 2, bottom: 5 }
          },
          scales: {
            xAxes: [{
              type: 'time',
              time: {
                unit: 'day',
                displayFormats: {
                  day: 'MMM DD'
                }
              },
              ticks: {
                display: true,
                fontColor: '#ffffff',
                fontSize: 9,
                maxRotation: 0,
                minRotation: 0,
                maxTicksLimit: 5
              },
              gridLines: {
                display: false
              }
            }],
            yAxes: [{
              ticks: {
                fontColor: '#ffffff',
                fontSize: 9,
                maxTicksLimit: 3
              },
              gridLines: {
                color: 'rgba(255, 255, 255, 0.1)',
                drawBorder: false
              }
            }]
          },
          legend: { display: false },
          maintainAspectRatio: false,
          responsive: true,
          tooltips: {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0,0,0,0.8)',
            titleFontSize: 10,
            bodyFontSize: 9
          }
        };
      },
      getChartOptions(showXAxis, yLabel) {
        return {
          layout: {
            padding: {
              left: 12,
              right: 12,
              top: 5,
              bottom: showXAxis ? 5 : 3
            }
          },
          scales: {
            xAxes: [
              {
                type: 'time',
                time: {
                  unit: 'day',
                  displayFormats: {
                    day: 'MMM DD',
                    hour: 'MMM DD HH:mm'
                  }
                },
                ticks: {
                  display: showXAxis,
                  fontColor: '#ffffff',
                  fontSize: 10,
                  maxRotation: 0,
                  minRotation: 0,
                  maxTicksLimit: 7
                },
                gridLines: {
                  display: false
                }
              },
            ],
            yAxes: [
              {
                ticks: {
                  fontColor: '#ffffff',
                  fontSize: 10,
                  maxTicksLimit: 3,
                  padding: 3
                },
                gridLines: {
                  color: 'rgba(255, 255, 255, 0.1)',
                  drawBorder: false
                },
                scaleLabel: {
                  display: true,
                  labelString: yLabel || '',
                  fontColor: '#ffffff',
                  fontSize: 11
                }
              },
            ],
          },
          legend: {
            display: false
          },
          maintainAspectRatio: false,
          responsive: true,
          tooltips: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0,0,0,0.8)',
            titleFontColor: '#ffffff',
            bodyFontColor: '#ffffff',
            titleFontSize: 11,
            bodyFontSize: 10
          }
        };
      },
      getAvailableCharts(device) {
        const charts = [];

        // Define all possible charts with their configuration and y-axis labels
        const chartConfigs = [
          { key: 'cpuTemp', data: device.cpuTempSeries, title: 'CPU Temperature', yLabel: '°C', color: '#ff5252' },
          { key: 'cpuFreq', data: device.cpuFreqSeries, title: 'CPU Frequency', yLabel: 'Frequency (MHz)', color: '#2196f3' },
          { key: 'ramdisk', data: device.ramdiskSeries, title: 'Ramdisk Usage', yLabel: 'Usage (%)', color: '#4caf50' },
          { key: 'signal', data: device.signalSeries, title: 'WiFi Signal', yLabel: 'dBm', color: '#9c27b0' },
          { key: 'availMem', data: device.availMemSeries, title: 'Available Memory', yLabel: 'Memory (MB)', color: '#00acc1' },
          { key: 'pyMem', data: device.pyMemSeries, title: 'Python Memory', yLabel: 'Memory (MB)', color: '#8e24aa' },
          { key: 'freeGb', data: device.freeGbSeries, title: 'Free Storage', yLabel: 'GB', color: '#fcba03' },
          { key: 'postTime', data: device.postTimeSeries, title: 'Post Time', yLabel: 'Time (ms)', color: '#ff9800' },
          { key: 'gridLength', data: device.gridLengthSeries, title: 'Grid Length', yLabel: 'Length', color: '#43a047' },
          { key: 'nDirect', data: device.nDirectSeries, title: 'Direct Detections', yLabel: 'Count', color: '#00bcd4' },
          { key: 'nCloud', data: device.nCloudSeries, title: 'Cloud Detections', yLabel: 'Count', color: '#795548' },
          { key: 'v', data: device.vSeries, title: 'Voltage', yLabel: 'V', color: '#e91e63' },
          { key: 'ovf', data: device.ovfSeries, title: 'Overflow', yLabel: 'Overflow Count', color: '#607d8b' }
        ];

        // Only include charts that have data
        chartConfigs.forEach(config => {
          if (config.data && config.data.length > 0) {
            charts.push(config);
          }
        });

        return charts;
      },
      formatLastSeen(timestamp) {
        return moment(timestamp).fromNow();
      },
      async getDevicesByOwner() {
        const devicesByOwner = await API.graphql({
          query: haikuboxByOwner,
          variables: { owner: this.user },
        });
        this.devicesByOwner = devicesByOwner.data.haikuboxByOwner.items;

        // Initialize device data and fetch status for each
        for (let i = 0; i < this.devicesByOwner.length; i++) {
          const device = this.devicesByOwner[i];

          // Initialize device properties
          this.$set(device, "componentKey", device.id);
          this.$set(device, "loading", true);
          this.$set(device, "hasData", false);

          // Get timezone from device lat/lon using tz-lookup
          let deviceTimezone = "America/New_York";
          if (device.lat != null && device.lon != null) {
            try {
              deviceTimezone = tzlookup(device.lat, device.lon);
            } catch (e) {
              // Keep default timezone if lookup fails
            }
          }
          this.$set(device, "timezone", deviceTimezone);
          this.$set(device, "currentWiFi", 0);
          this.$set(device, "currentFreeGb", null);
          this.$set(device, "currentVoltage", null);
          this.$set(device, "isConnected", false);
          this.$set(device, "lastStatusTime", null);
          this.$set(device, "version", device.version || "Unknown");

          // Initialize time series arrays
          this.$set(device, "cpuTempSeries", []);
          this.$set(device, "cpuFreqSeries", []);
          this.$set(device, "ramdiskSeries", []);
          this.$set(device, "signalSeries", []);
          this.$set(device, "nDirectSeries", []);
          this.$set(device, "nCloudSeries", []);
          this.$set(device, "freeGbSeries", []);
          this.$set(device, "vSeries", []);
          this.$set(device, "ovfSeries", []);
          this.$set(device, "postTimeSeries", []);
          this.$set(device, "availMemSeries", []);
          this.$set(device, "pyMemSeries", []);
          this.$set(device, "gridLengthSeries", []);

          // Fetch status for each device
          try {
            await this.getStatus(device.serial, i);
          } catch (error) {
            console.error(`Error fetching data for device ${device.serial}:`, error);
          } finally {
            this.$set(device, "loading", false);
          }
        }

        this.numberOfDevices = this.devicesByOwner.length;
        this.allowDeleteAccount = this.numberOfDevices > 0;
        this.haikuboxesLoaded = true;

        // console.log("settings: devices by owner:", this.devicesByOwner);
      },
      async getStatus(serialClicked, deviceIndex) {
        const device = this.devicesByOwner[deviceIndex];
        var nextToken = null;
        var haikuboxStatus = [];

        // Only fetch data within the lookback window
        const oneWeekAgo = moment.utc().subtract(this.statusLookbackDays, "days").format();

        do {
          const filterArg = {
            serial: serialClicked,
            createdAt: { gt: oneWeekAgo },
            nextToken: nextToken,
          };
          const boxStatus = await API.graphql({
            query: statusByDate,
            variables: filterArg,
          });
          haikuboxStatus = haikuboxStatus.concat(
            boxStatus.data.statusByDate.items
          );
          nextToken = boxStatus.data.statusByDate.nextToken;
        } while (nextToken != null);

        // Sort by createdAt to ensure chronological order for charts
        haikuboxStatus.sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)));

        // Process the status data
        var cpuTempSeries = [];
        var cpuFreqSeries = [];
        var ramdiskSeries = [];
        var signalSeries = [];
        var nDirectSeries = [];
        var nCloudSeries = [];
        var freeGbSeries = [];
        var vSeries = [];
        var ovfSeries = [];
        var postTimeSeries = [];
        var availMemSeries = [];
        var pyMemSeries = [];
        var gridLengthSeries = [];
        var currentWiFi = 0;
        var lastStatusTime = null;

        // Get the most recent status for connection check and version
        var currentFreeGb = null;
        var currentVoltage = null;
        if (haikuboxStatus.length > 0) {
          const mostRecent = haikuboxStatus[haikuboxStatus.length - 1];
          lastStatusTime = mostRecent.createdAt;
          currentWiFi = mostRecent.signal != null ? mostRecent.signal : 0;
          currentFreeGb = mostRecent.freeGB != null ? mostRecent.freeGB : null;
          currentVoltage = mostRecent.V != null ? mostRecent.V : null;

          // Get firmware version from most recent status that has it
          for (let i = haikuboxStatus.length - 1; i >= 0; i--) {
            if (haikuboxStatus[i].version) {
              this.$set(device, "version", haikuboxStatus[i].version);
              break;
            }
          }
        }

        for (var entry in haikuboxStatus) {
          const status = haikuboxStatus[entry];

          // Use Date object for proper time axis formatting
          var datetimeX = moment.utc(status.createdAt).toDate();

          // Always push data for fields that are typically present
          if (status.cpuTemp != null) cpuTempSeries.push([datetimeX, status.cpuTemp]);
          if (status.cpuFreq != null) cpuFreqSeries.push([datetimeX, status.cpuFreq]);
          if (status.ramdisk != null) ramdiskSeries.push([datetimeX, status.ramdisk]);
          if (status.signal != null) signalSeries.push([datetimeX, status.signal]);
          if (status.nDirect != null) nDirectSeries.push([datetimeX, status.nDirect]);
          if (status.nCloud != null) nCloudSeries.push([datetimeX, status.nCloud]);
          if (status.freeGB != null) freeGbSeries.push([datetimeX, status.freeGB]);
          if (status.V != null) vSeries.push([datetimeX, status.V]);
          if (status.ovf != null) ovfSeries.push([datetimeX, status.ovf]);
          if (status.postTime != null) postTimeSeries.push([datetimeX, status.postTime]);
          if (status.availMem != null) availMemSeries.push([datetimeX, status.availMem]);
          if (status.pyMem != null) pyMemSeries.push([datetimeX, status.pyMem]);
          if (status.gridLength != null) gridLengthSeries.push([datetimeX, status.gridLength]);
        }

        // Update device with time series data
        this.$set(device, "cpuTempSeries", cpuTempSeries);
        this.$set(device, "cpuFreqSeries", cpuFreqSeries);
        this.$set(device, "ramdiskSeries", ramdiskSeries);
        this.$set(device, "signalSeries", signalSeries);
        this.$set(device, "nDirectSeries", nDirectSeries);
        this.$set(device, "nCloudSeries", nCloudSeries);
        this.$set(device, "freeGbSeries", freeGbSeries);
        this.$set(device, "vSeries", vSeries);
        this.$set(device, "ovfSeries", ovfSeries);
        this.$set(device, "postTimeSeries", postTimeSeries);
        this.$set(device, "availMemSeries", availMemSeries);
        this.$set(device, "pyMemSeries", pyMemSeries);
        this.$set(device, "gridLengthSeries", gridLengthSeries);
        this.$set(device, "currentWiFi", currentWiFi);
        this.$set(device, "currentFreeGb", currentFreeGb);
        this.$set(device, "currentVoltage", currentVoltage);
        this.$set(device, "hasData", haikuboxStatus.length > 0);

        // Check if device is connected (last reading within 2 weeks for status display)
        // But show as disconnected if no data at all or very stale
        const connectionTimeoutMinutes = 30; // Device considered disconnected after 30 min
        if (lastStatusTime) {
          const minutesSinceLastStatus = moment().diff(moment(lastStatusTime), 'minutes');
          this.$set(device, "isConnected", minutesSinceLastStatus <= connectionTimeoutMinutes);
          this.$set(device, "lastStatusTime", lastStatusTime);
        } else {
          this.$set(device, "isConnected", false);
        }
      },
      async refreshPage() {
        await Auth.signOut();
        this.$router.push({ path: "/" });
        location.reload();
      },
    },
  };
  </script>

  <style scoped>
  .v-card {
    overflow: visible;
  }
  .chart-container {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 4px;
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .chart-container:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
    transition: all 0.2s ease;
  }
  .chart-container-compact {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 4px;
    padding: 4px 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .chart-title {
    font-size: 13px;
    font-weight: 400;
    color: #ffffff;
    margin-bottom: 4px;
    opacity: 0.95;
  }
  .chart-title-compact {
    font-size: 11px;
    font-weight: 400;
    color: #ffffff;
    margin-bottom: 2px;
    opacity: 0.8;
  }
  .overview-chip {
    cursor: pointer;
    height: auto !important;
    min-height: 24px;
    padding-top: 3px;
    padding-bottom: 3px;
  }
  .overview-chip:hover {
    opacity: 0.9;
  }
  .overview-chip >>> .v-chip__content {
    display: block;
    white-space: normal;
    line-height: 1.3;
  }
  .chip-stats {
    font-size: 11px;
    opacity: 0.9;
    margin-left: 20px; /* align under name, past the icon */
  }
  </style>
